import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardCheck, GraduationCap } from "lucide-react";
import StaffCheckInCard from "@/components/app/StaffCheckInCard";

// -------- Staff Home --------
export function StaffHome() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState({});
  useEffect(() => { api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
          {t("hello")}, {user?.name.split(" ")[0]}
        </h1>
        <p className="text-muted-foreground">{t("staff_dashboard_title")}</p>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <StaffCheckInCard />
        <Card className="rounded-2xl"><CardContent className="p-6">
          <h2 className="font-display text-xl font-bold">Today at the center</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div><div className="text-sm text-muted-foreground">{t("students")} today</div><div className="num text-3xl font-semibold">{summary.students_today ?? 0}</div></div>
            <div><div className="text-sm text-muted-foreground">Total {t("students")}</div><div className="num text-3xl font-semibold">{summary.total_students ?? 0}</div></div>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

// -------- Teacher Home --------
export function TeacherHome() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState({});
  useEffect(() => { api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
          {t("hello")}, {user?.name.split(" ")[0]}
        </h1>
        <p className="text-muted-foreground">{t("teacher_dashboard_title")}</p>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <StaffCheckInCard />
        <Card className="rounded-2xl"><CardContent className="p-6">
          <h2 className="font-display text-xl font-bold">Marked today</h2>
          <div className="mt-2 num text-5xl font-semibold">{summary.attendance_marked_today ?? 0}</div>
          <p className="mt-2 text-sm text-muted-foreground">Head to Attendance to tick more.</p>
        </CardContent></Card>
      </div>
    </div>
  );
}

// -------- Student attendance page (staff+teacher) --------
export function StudentAttendancePage() {
  const { t } = useI18n();
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState({});
  const [busyId, setBusyId] = useState(null);

  const loadAll = () => {
    api.get("/students").then((r) => setStudents(r.data.students));
    api.get("/attendance/student").then((r) => setRecords(r.data.records));
  };
  useEffect(() => { loadAll(); }, []);

  const mark = async (student_id, statusVal) => {
    setBusyId(student_id);
    try {
      await api.post("/attendance/student/manual", {
        student_id,
        status: statusVal,
      });
      toast.success("Marked");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-8" data-testid="teacher-attendance">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("markAttendance")}</h1>
        <p className="num text-sm text-muted-foreground">{today}</p>
      </div>

      <Card className="rounded-2xl"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("fullName")}</TableHead>
            <TableHead>{t("level")}</TableHead>
            <TableHead className="w-72">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {students.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.full_name}</TableCell>
                <TableCell className="num">{s.level}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {[
                      { v: "present", label: t("present") },
                      { v: "late", label: t("late") },
                      { v: "absent", label: t("absent") },
                    ].map((o) => (
                      <Button
                        key={o.v}
                        size="sm"
                        variant={o.v === "present" ? "default" : o.v === "late" ? "secondary" : "outline"}
                        onClick={() => mark(s.id, o.v)}
                        disabled={busyId === s.id}
                        className="rounded-full"
                        data-testid={`mark-${o.v}-${s.id}`}
                      >
                        {o.label}
                      </Button>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <section>
        <h2 className="mb-3 font-display text-xl font-bold">Recent</h2>
        <Card className="rounded-2xl"><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{records.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.student_name}</TableCell>
                <TableCell className="num">{r.method}</TableCell>
                <TableCell><span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{r.status}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{records.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
        </CardContent></Card>
      </section>
    </div>
  );
}
